import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useFormik } from 'formik';

import { Button, CheckboxField, Icon, Modal, Text, TextField } from '../../common/components';
import { t } from '../../common/i18n';
import Yup from '../../common/utils/FormValidation';
import { resolveCategoryIcon } from '../config/issueConfig';
import { IssueCategory, IssueCategoryFormValues } from '../types';
import IssueCategoryIconPicker from './IssueCategoryIconPicker';

const namespace = 'Issues';

interface IssueCategorySettingsModalProps {
  isShown: boolean;
  /** Kategorie v admin tvaru (s kontakty a příznaky isDefault/used). */
  categories: IssueCategory[];
  onHide: () => void;
  onCreate: (values: IssueCategoryFormValues) => Promise<void>;
  onUpdate: (id: string, values: IssueCategoryFormValues) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onReorder: (categoryIds: string[]) => Promise<void>;
}

const validationSchema = Yup.object({
  name: Yup.string().required(),
});

export const IssueCategorySettingsModal: React.FC<IssueCategorySettingsModalProps> = ({
  isShown,
  categories,
  onHide,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
}) => {
  // null = seznam, 'new' = přidání, jinak editovaná kategorie
  const [editing, setEditing] = useState<IssueCategory | 'new' | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Drag & drop řazení – během tažení držíme náhledové pořadí id lokálně,
  // commit na server až v onDragEnd (HTML5 DnD; na dotykových zařízeních
  // nefunguje, tam zůstává pořadí dané výborem z desktopu).
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [previewIds, setPreviewIds] = useState<string[] | null>(null);
  // Guard proti opakovaným dragenter (bublají z vnořených elementů řádku) –
  // bez něj se seznam při tažení „třese".
  const lastOverIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isShown) {
      setEditing(null);
      setDraggedId(null);
      setPreviewIds(null);
    }
  }, [isShown]);

  const orderedCategories = useMemo(() => {
    if (!previewIds) return categories;
    const byId = new Map(categories.map((item) => [item.id, item]));
    return previewIds.map((id) => byId.get(id)).filter(Boolean) as IssueCategory[];
  }, [categories, previewIds]);

  // FLIP animace: při přeskupení seznamu řádky plynule dojedou na nové místo
  // (invertovaný translateY → transition na 0), místo skokové změny.
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const prevTopsRef = useRef(new Map<string, number>());

  useLayoutEffect(() => {
    const newTops = new Map<string, number>();

    itemRefs.current.forEach((element, id) => {
      const top = element.getBoundingClientRect().top;
      newTops.set(id, top);

      const prevTop = prevTopsRef.current.get(id);
      if (prevTop !== undefined && prevTop !== top) {
        element.style.transition = 'none';
        element.style.transform = `translateY(${prevTop - top}px)`;
        requestAnimationFrame(() => {
          element.style.transition = 'transform 180ms ease';
          element.style.transform = '';
        });
      }
    });

    prevTopsRef.current = newTops;
  }, [orderedCategories]);

  const handleDragStart = (event: React.DragEvent, id: string) => {
    // setData je nutné pro Firefox, jinak drag vůbec nezačne
    event.dataTransfer.setData('text/plain', id);
    event.dataTransfer.effectAllowed = 'move';
    lastOverIdRef.current = null;
    setDraggedId(id);
    setPreviewIds(categories.map((item) => item.id));
  };

  const handleDragEnter = (overId: string) => {
    if (overId === lastOverIdRef.current) return;
    lastOverIdRef.current = overId;

    if (!draggedId || draggedId === overId || !previewIds) return;

    const ids = previewIds.filter((id) => id !== draggedId);
    ids.splice(ids.indexOf(overId), 0, draggedId);
    setPreviewIds(ids);
  };

  const handleDragEnd = async () => {
    const ids = previewIds;
    setDraggedId(null);
    setPreviewIds(null);
    lastOverIdRef.current = null;

    if (ids && ids.join() !== categories.map((item) => item.id).join()) {
      await onReorder(ids);
    }
  };

  const selected = editing !== null && editing !== 'new' ? editing : null;

  const formik = useFormik<IssueCategoryFormValues>({
    enableReinitialize: true,
    initialValues: {
      name: selected?.name ?? '',
      icon: selected ? resolveCategoryIcon(selected.icon) : 'info',
      contactEmails: [
        selected?.contactEmails?.[0] ?? '',
        selected?.contactEmails?.[1] ?? '',
        selected?.contactEmails?.[2] ?? '',
      ],
      active: selected?.active ?? true,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (selected) {
          await onUpdate(selected.id, values);
        } else {
          await onCreate(values);
        }
        setEditing(null);
      } finally {
        formik.setSubmitting(false);
      }
    },
  });

  const handleDelete = async () => {
    if (!selected) return;
    setIsDeleting(true);
    try {
      await onDelete(selected.id);
      setEditing(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const setContactEmail = (index: number, value: string) => {
    const emails = [...formik.values.contactEmails];
    emails[index] = value;
    formik.setFieldValue('contactEmails', emails);
  };

  const canDelete = selected !== null && !selected.isDefault && !selected.used;

  return (
    <Modal isShown={isShown} onHide={onHide}>
      {editing === null && (
        <>
          <Text size="h2" align="center" className="mb-2">
            {t('Settings.title', namespace)}
          </Text>
          <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
            {t('Settings.subtitle', namespace)}
          </Text>

          {/* dragover s preventDefault na celém kontejneru: bez něj browser v mezerách
              mezi řádky bliká kurzorem „zákaz" (drop tam není povolený) */}
          <div
            className="IssueCategoryList"
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
            }}
          >
            {orderedCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                ref={(element) => {
                  if (element) itemRefs.current.set(category.id, element);
                  else itemRefs.current.delete(category.id);
                }}
                className={cx(
                  'IssueCategoryList__item',
                  draggedId === category.id && 'IssueCategoryList__item--dragging'
                )}
                draggable
                onDragStart={(event) => handleDragStart(event, category.id)}
                onDragEnter={() => handleDragEnter(category.id)}
                onDragOver={(event) => {
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDragEnd={handleDragEnd}
                onClick={() => setEditing(category)}
              >
                <span className="IssueCategoryList__grip" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="IssueCategoryList__icon">
                  <Icon name={resolveCategoryIcon(category.icon)} />
                </span>
                <span className="IssueCategoryList__body">
                  <Text as="span" size="body-medium" weight="600" color={category.active ? 'Neutral800' : 'Neutral600'}>
                    {category.name}
                  </Text>
                  <Text as="span" size="body-small" color="Neutral600">
                    {category.contactEmails && category.contactEmails.length > 0
                      ? category.contactEmails.join(', ')
                      : t('Settings.noContacts', namespace)}
                  </Text>
                </span>
                {!category.active && <span className="IssueCategoryList__inactive">{t('Settings.inactive', namespace)}</span>}
                <Icon name="edit" className="IssueCategoryList__edit" />
              </button>
            ))}
          </div>

          <Text as="div" size="body-small" color="Neutral600" className="mt-1 mb-1">
            {t('Settings.reorderNote', namespace)}
          </Text>
          <Text as="div" size="body-small" color="Neutral600" className="mb-3">
            {t('Settings.boardCopyNote', namespace)}
          </Text>

          <div className="Flex FlexAlign--center FlexJustify--center">
            <Button title={t('Settings.addAction', namespace)} onClick={() => setEditing('new')} wide />
          </div>
        </>
      )}

      {editing !== null && (
        <>
          <button type="button" className="IssueCategorySettings__backlink" onClick={() => setEditing(null)}>
            <Icon name="arrowLeft" />
            <span>{t('Settings.backAction', namespace)}</span>
          </button>

          <Text size="h2" align="center" className="mb-3">
            {selected ? t('Settings.editTitle', namespace) : t('Settings.createTitle', namespace)}
          </Text>

          <form onSubmit={formik.handleSubmit}>
            <TextField
              label={t('Settings.Fields.name.label', namespace)}
              placeholder={t('Settings.Fields.name.placeholder', namespace)}
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={{ message: formik.errors.name, touched: formik.touched.name }}
              className="mb-2"
              required
            />

            <Text as="div" size="body-small" weight="500" className="mb-1">
              {t('Settings.Fields.icon.label', namespace)}
            </Text>
            <IssueCategoryIconPicker
              value={formik.values.icon}
              onChange={(icon) => formik.setFieldValue('icon', icon)}
            />

            <Text as="div" size="body-small" weight="500" className="mt-2 mb-1">
              {t('Settings.Fields.contacts.label', namespace)}
            </Text>
            <Text as="div" size="body-small" color="Neutral600" className="mb-2">
              {t('Settings.Fields.contacts.note', namespace)}
            </Text>
            {[0, 1, 2].map((index) => (
              <TextField
                key={index}
                label=""
                placeholder={t('Settings.Fields.contacts.placeholder', namespace)}
                name={`contactEmails.${index}`}
                value={formik.values.contactEmails[index]}
                onChange={(event) => setContactEmail(index, event.target.value)}
                className="mb-1"
              />
            ))}

            {selected && (
              <CheckboxField
                variant="switch"
                name="active"
                label={t('Settings.Fields.active.label', namespace)}
                checked={formik.values.active}
                onChange={(event) => formik.setFieldValue('active', event.target.checked)}
                className="mt-2 mb-2"
              />
            )}

            {selected && !canDelete && (
              <Text as="div" size="body-small" color="Neutral600" className="mb-2">
                {t(selected.isDefault ? 'Settings.defaultHint' : 'Settings.usedHint', namespace)}
              </Text>
            )}

            <div className="IssueCategorySettings__actions mt-3">
              <Button
                onClick={formik.handleSubmit}
                loading={formik.isSubmitting}
                disabledDeps={['demo', 'expired']}
                wide
                title={t(selected ? 'Settings.submitEdit' : 'Settings.submitCreate', namespace)}
              />
              {canDelete && (
                <Button
                  variant="text"
                  title={t('Settings.deleteAction', namespace)}
                  loading={isDeleting}
                  disabledDeps={['demo', 'expired']}
                  onClick={handleDelete}
                />
              )}
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default IssueCategorySettingsModal;
